import React, { useEffect, useState } from 'react';
import {
  Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Button, Flex, HStack, Image, Skeleton, Tag, TagLabel,
  Text, Tooltip, useColorModeValue, useToast,
} from '@chakra-ui/react';
import {
  FiChevronRight, FiFileText, FiFolder, FiRotateCcw, FiRadio,
} from 'react-icons/fi';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { NIL } from 'uuid';
import useProject from '../../hooks/useProject';
import { useProjectContracts } from '../../hooks/useContracts'; // item 1432 — awaiting-signature pill
import { ContractStatus } from '../../models/contract';
import useCommissions from '../../hooks/useCommissions'; // item 1170 — project gross profit
import useRecurringRevenues from '../../hooks/useRecurringRevenues'; // item 1170 — sub->project map
import { CommissionStatus } from '../../models/commission';
import useCurrentUser from '../../hooks/useCurrentUser';
import { useRnwyFleet, rnwyHealth, rnwyForProject } from '../../hooks/useRnwyStatus'; // item 861/1124
import RnwyLinkModal from '../RnwyLinkModal'; // item 861
import { ProjectStatus } from '../../models/project';
import { ProposalStatus } from '../../models/proposal';
import { useAppearance } from '../../theme/AppearanceProvider';
import { useProjectMode } from '../../theme/ProjectModeProvider';
import IconProjects from '../../assets/Icon-Projects.svg';
import IconProjectsWhite from '../../assets/Icon-Projects-White.svg';
import rnwyWordmark from '../../assets/rnwy-wordmark-purple.png'; // item 875 — masked + tinted
import openPdf from '../../utils/openPdf';
import { money } from '../../utils/money';

// item 104 + breadcrumbs: a slim PROJECT CONTEXT bar that keeps you "inside" a
// project. It shows whenever a project is the active context (set when you open
// /project/:id, persisted across its doc sub-views via ProjectModeProvider), with
// breadcrumbs back to the dashboard + project. Null in ala-carte (Basix Six) mode.

const STATUS: Record<number, { label: string; scheme: string; glow: string }> = {
  [ProjectStatus.Open]: { label: 'Open', scheme: 'green', glow: 'rgba(72, 187, 120, 0.45)' },
  [ProjectStatus.Proposed]: { label: 'Proposed', scheme: 'orange', glow: 'rgba(237, 137, 54, 0.45)' },
  [ProjectStatus.Closed]: { label: 'Closed', scheme: 'gray', glow: 'rgba(160, 174, 192, 0.30)' },
};

// the current doc sub-view's breadcrumb label, by route prefix
const docLabelFor = (pathname: string): string | null => {
  const p = pathname.toLowerCase();
  if (p.startsWith('/proposal/')) return 'Proposal';
  if (p.startsWith('/workorder/')) return 'Work Order';
  if (p.startsWith('/invoice/')) return 'Invoice';
  if (p.startsWith('/bill/')) return 'Bill';
  if (p.startsWith('/purchaseorder/')) return 'Purchase Order';
  return null;
};

const ProjectContextBar: React.FC = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const match = useRouteMatch<{ id: string }>({ path: '/project/:id', exact: false });
  const urlProjectId = match && match.params.id !== 'edit' ? match.params.id : null;

  const { activeProject, enterProject } = useProjectMode();
  // the project to show: the one in the URL, else the sticky active one
  const projectId = urlProjectId ?? activeProject?.id ?? NIL;
  const { data, status, setProjectReopened } = useProject({ id: projectId });
  // item 1432 (Chad) — papers OUT FOR SIGNATURE at SignWell (Status = Sent); the same query the
  // CONTRACTS section uses, so no extra request on the project page.
  const { data: contractRows } = useProjectContracts(projectId && projectId !== NIL ? projectId : undefined);
  const awaitingSignature = (contractRows?.index ?? []).filter((c) => c.status === ContractStatus.Sent && !c.isVoid).length;

  // entering a project route makes it the active (sticky) context
  useEffect(() => {
    if (urlProjectId && data && data.id === urlProjectId) {
      enterProject({ id: data.id, name: data.name });
    }
  }, [urlProjectId, data, enterProject]);

  const { aurora } = useAppearance();
  const { isAdmin, isSuperAdmin } = useCurrentUser();
  // item 1170 — GP-pill data. HOOKS MUST SIT ABOVE the early return below: this bar
  // conditionally renders null, and hooks after that return crash with React #310
  // ("rendered more hooks…") the moment the bar toggles (2026-08-16, Chad's screenshot).
  const { data: commissionData } = useCommissions();
  const { data: recurringData } = useRecurringRevenues();
  const isAdminEyes = isAdmin || isSuperAdmin;
  const toast = useToast();
  // item 861 — live RNWY capacity for THIS project (one shared fleet query app-wide)
  const { data: fleet } = useRnwyFleet();
  const [rnwyLinkOpen, setRnwyLinkOpen] = useState(false);
  const glassBg = useColorModeValue('whiteAlpha.700', 'whiteAlpha.100');
  const borderColorGlass = useColorModeValue('blackAlpha.200', 'whiteAlpha.200');
  const subColorGlass = useColorModeValue('gray.500', 'whiteAlpha.600');
  const crumbColorGlass = useColorModeValue('gray.600', 'whiteAlpha.700');
  const nameColorGlass = useColorModeValue('gray.800', 'whiteAlpha.900');
  const bg = aurora ? glassBg : 'ledgerSurface';
  const borderColor = aurora ? borderColorGlass : 'ledgerStroke';
  const subColor = aurora ? subColorGlass : 'ledgerMuted';
  const crumbColor = aurora ? crumbColorGlass : 'ledgerMuted';
  const nameColor = aurora ? nameColorGlass : 'ledgerInk';
  // item 958 — light-mode contrast: the bar's hairline edge + shadow were the dark-canvas recipe in both
  // color modes (invisible edge / muddy halo on the light canvas). Dark keeps today's values.
  const shellBorder = useColorModeValue('blackAlpha.200', 'whiteAlpha.300');
  const shellShadow = useColorModeValue(
    '0 8px 24px rgba(0,0,0,0.10)',
    '0 10px 40px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.16)',
  );
  const projectIcon = useColorModeValue(IconProjects, IconProjectsWhite);

  // item 732 (Chad) — only show INSIDE a project: on the project landing (/project/:id) or a
  // document DETAIL route (proposal/invoice/workorder/bill/PO). Top-level nav pages — Subscriptions,
  // the doc LISTS, dashboard, CRM, etc. — must NOT show a stale project context, even though
  // activeProject stays sticky so in-project navigation still works.
  const onDocDetail = docLabelFor(pathname) !== null;
  if (!urlProjectId && !(onDocDetail && activeProject)) return null;

  const name = data?.name ?? activeProject?.name ?? '';
  const ctxId = data?.id ?? activeProject?.id ?? '';
  const onProjectLanding = !!urlProjectId;
  const docLabel = onProjectLanding ? null : docLabelFor(pathname);
  const st = data ? STATUS[data.status] : undefined;
  // item 691 (Chad) — running total of all FINALIZED (non-void) invoices on the project, shown in
  // the header. item 713 — exclude ARCHIVED too; the backend sends `isArchive` while the model calls
  // it `isArchived`, so check BOTH (same reason the grids check both — item 712).
  const finalizedInvoiced = ((data as any)?.invoices ?? [])
    .filter((inv: any) => inv?.isFinalized && !inv?.isVoid && !inv?.isArchive && !inv?.isArchived)
    .reduce((sum: number, inv: any) => sum + (inv?.retailTotal ?? 0), 0);
  // item 703 (Chad) — total of PENDING proposals on the project (same amount logic as the proposals grid:
  // RNWY → perpetual amount, else the retail total).
  const proposalAmount = (p: any): number => {
    const isRnwy = !!p.isRnwy || p.kind === 1 || p.kind === 2;
    if (isRnwy && p.rnwyPerpetualAmount != null) return p.rnwyPerpetualAmount;
    return p.retailProposalTotal ?? p.retailTotal ?? 0;
  };
  // item 713 (Chad) — exclude archived (check both isArchive/isArchived) + void from the PENDING total
  // item 868b — an unsent customer request isn't OPPORTUNITY yet (no numbers until we send)
  const pendingProposals = ((data as any)?.proposals ?? [])
    .filter((p: any) => !p.isVoid && !p.isArchive && !p.isArchived && p.status === ProposalStatus.Pending
      && !p.isContractOnly // item 1180d — paper-only is not an opportunity
      && !(p.isCustomerSubmission && !p.sentDate));
  const pendingTotal = pendingProposals.reduce((sum: number, p: any) => sum + proposalAmount(p), 0);
  // item 760 (Chad) — the WON-but-unbilled remainder (Approved = full, In Progress = final 50%):
  // "outstanding progress bills attached to a proposal that have not been billed yet" — a key
  // pipeline metric, stamped on the header so it's never invisible.
  const toBill = ((data as any)?.proposals ?? [])
    .filter((p: any) => !p.isVoid && !p.isArchive && !p.isArchived && !p.isContractOnly) // item 1180d
    .reduce((sum: number, p: any) => {
      if (p.status === ProposalStatus.Approved) return sum + proposalAmount(p);
      if (p.status === ProposalStatus.ApprovedDeposited) return sum + proposalAmount(p) / 2;
      return sum;
    }, 0);
  // item 1170 (Chad: "we could add a gross profit that subtracts all commissions") — JOB
  // COSTING v1: GP = finalized invoiced − every commission the project generated (one-time
  // rows via its proposals + recurring rows via its subscriptions). Reversed/Cancelled rows
  // are out (1167), so reversing a commission restores the GP instantly. Admin eyes only —
  // non-admins receive just their own commission rows, which would overstate GP.
  const projectProposalIds = new Set(((data as any)?.proposals ?? []).map((p: any) => p.id));
  const projectSubIds = new Set((recurringData?.index ?? [])
    .filter((r: any) => r.projectId === data?.id).map((r: any) => r.id));
  // item 1212 (Chad: "GP should update from work orders — the free understanding of the
  // project billables") — the WORK ORDER is the job spine: commissions on the project's
  // WOs and invoices count even when there's no proposal behind them (standalone service
  // jobs, ad-hoc splits, manual entries booked to a job, the M3 AR imports).
  const projectWoNumbers = new Set(((data as any)?.workOrders ?? [])
    .map((w: any) => w.workOrderNumber).filter(Boolean));
  const projectInvoiceNumbers = new Set(((data as any)?.invoices ?? [])
    .map((i: any) => i.invoiceNumber).filter(Boolean));
  const projectCommissions = isAdminEyes
    ? (commissionData?.index ?? [])
      .filter((c: any) => c.status !== CommissionStatus.Cancelled && c.status !== CommissionStatus.Reversed
        && ((c.proposalId && projectProposalIds.has(c.proposalId))
          || (c.recurringRevenueId && projectSubIds.has(c.recurringRevenueId))
          || (c.workOrderNumber && projectWoNumbers.has(c.workOrderNumber))
          || (c.invoiceNumber && projectInvoiceNumbers.has(c.invoiceNumber))))
      .reduce((sum: number, c: any) => sum + (c.amount ?? 0), 0)
    : 0;
  // item 1292 (Chad: "subtract distributor cost from hardware sales — GP shows the
  // difference between grp and distro") — JOB COSTING v2: the hardware on a finalized
  // invoice was BOUGHT at distro, so its cost comes off GP alongside commissions.
  // Same invoice set as INVOICED (finalized, non-void, non-archived); hardware lines
  // only — a service line's cost is people, which the commission subtraction owns.
  // item 1298 (Chad: GP still high — a fresh invoice minted with a stale IsHardware=0
  // again, hiding \$498.57 of real cost) — the flag keeps rotting at mint time, so the
  // COST is the signal now: any line carrying costEach > 0 records money we actually
  // paid (revenue-side lines only carry cost when there was a buy — item 425), and it
  // comes off GP whether or not the flag survived the copy chain.
  // item 1300 (Chad: "the imported AR items will just be ignored") — GP is a NEW-ERA
  // margin number: QB-backfill imports carry revenue but no recorded cost, so they'd
  // read as pure profit. They leave the GP math entirely (revenue AND cost side),
  // using the same marker the month-revenue KPI uses (1227/1228 — QuickBooksReference,
  // NOT suppressCommissions, which Chad also sets on real new work). The INVOICED pill
  // still shows everything billed — that's an AR fact, not a margin claim.
  const gpInvoices = ((data as any)?.invoices ?? [])
    .filter((inv: any) => inv?.isFinalized && !inv?.isVoid && !inv?.isArchive && !inv?.isArchived
      && inv?.quickBooksReference !== 'QB backfill');
  const gpInvoiced = gpInvoices.reduce((sum: number, inv: any) => sum + (inv?.retailTotal ?? 0), 0);
  const hardwareCost = gpInvoices
    .reduce((sum: number, inv: any) => sum + ((inv?.invoiceItems ?? [])
      .filter((it: any) => it?.isHardware || (it?.costEach ?? 0) > 0)
      .reduce((s: number, it: any) => s + (it?.costEach ?? 0) * (it?.quantity ?? 0), 0)), 0);
  const grossProfit = gpInvoiced - projectCommissions - hardwareCost;

  // item 861 (Chad) — live RNWY venue capacity as a KPI chip in the header (like the persistent
  // dash): running/total + open slots, traffic-lit by headroom. Admins click it to relink; an
  // unlinked project shows a LINK RNWY affordance (admins, project landing only).
  // item 1124 — multi-processor projects: aggregate the project's fleet rows (sums across
  // boxes; ok only when every box answered)
  const rnwyRow = rnwyForProject(fleet, ctxId);
  const rnwySystemId = (data as any)?.rnwySystemId ?? (rnwyRow ? rnwyRow.systems.join(',') : null);
  const rnwyScheme = rnwyRow && rnwyRow.ok
    ? ({ green: 'green', amber: 'orange', red: 'red' } as const)[rnwyHealth(rnwyRow.venueCapacity)]
    : 'gray';
  const rnwyGlow = {
    green: 'rgba(72,187,120,0.45)', orange: 'rgba(237,137,54,0.45)', red: 'rgba(229,62,62,0.45)', gray: 'rgba(160,174,192,0.30)',
  }[rnwyScheme];
  // item 875 (Chad) — ONE pill family for the whole header (money, RNWY, status): the same
  // quiet Tag the STATUS pill uses, color-coded, no rotation. All the info, one visual language.
  const PILL_GLOW: Record<string, string> = {
    orange: 'rgba(237,137,54,0.45)', red: 'rgba(229,62,62,0.45)', green: 'rgba(72,187,120,0.45)', gray: 'rgba(160,174,192,0.30)',
  };
  const headerPill = (scheme: string, label: React.ReactNode) => (
    <Tag
      size="sm"
      colorScheme={scheme}
      variant="subtle"
      borderRadius="xl"
      px={2}
      py={1}
      flexShrink={0}
      fontSize="0.68rem"
      fontWeight="bold"
      letterSpacing="0.04em"
      whiteSpace="nowrap"
      boxShadow={aurora ? `0 0 12px ${PILL_GLOW[scheme] ?? PILL_GLOW.gray}` : 'none'}
    >
      <TagLabel>{label}</TagLabel>
    </Tag>
  );
  // item 875/876 — the RNWY chip's hover: everything the status API gives us, in Chad's
  // explicit vocabulary (total allowed / in use / remaining).
  const rnwyTipLabel = rnwyRow ? (
    <Box fontSize="xs">
      <Text fontWeight="bold">{`RNWY system${rnwyRow.systems.length > 1 ? 's' : ''}: ${rnwyRow.systems.join(' · ')}`}</Text>
      {/* item 1124 — per-box breakdown when a property runs more than one processor */}
      {rnwyRow.rows.length > 1 && rnwyRow.rows.map((r) => (
        <Text key={r.rnwySystemId}>
          {r.ok
            ? `${r.rnwySystemId}: ${r.venuesRunning} in use · ${r.venueCapacity} of ${r.venueTotalCapacity} open`
            : `${r.rnwySystemId}: live status unavailable`}
        </Text>
      ))}
      {rnwyRow.ok && <Text>{`Total venues allowed: ${rnwyRow.venueTotalCapacity}`}</Text>}
      {rnwyRow.ok && <Text>{`In use: ${rnwyRow.venuesRunning}`}</Text>}
      {rnwyRow.ok && <Text>{`Remaining: ${rnwyRow.venueCapacity}`}</Text>}
      {!rnwyRow.ok && <Text>Live status unavailable right now</Text>}
      {isAdminEyes && <Text opacity={0.7} mt={1}>Click to relink</Text>}
    </Box>
  ) : undefined;

  return (
    <Box mb={{ base: 3, md: 4 }}>
      <Flex
        direction="column"
        gap={{ base: 1.5, md: 2 }}
        px={{ base: 3, md: 5 }}
        py={{ base: 2, md: 2.5 }}
        borderRadius="2xl"
        bg={bg}
        border="1px solid"
        borderColor={aurora ? shellBorder : borderColor} // item 958 — light-mode contrast
        backdropFilter={aurora ? 'var(--bx-glass-panel)' : undefined}
        boxShadow={aurora ? shellShadow : 'none'}
      >
        {/* item 1063 (Chad: tablet pass) — the header WRAPS below lg so the money pills drop
            to a second line instead of crushing the project name into a vertical stack */}
        <Flex align="center" w="full" wrap={{ base: 'wrap', lg: 'nowrap' }} sx={{ rowGap: '8px' }}>
          {/* item 957 (Chad) — the legacy Icon-Projects.svg glyph read as visual noise here;
              a crisp brand-tinted folder marks "you're inside this project" instead. */}
          <Box as={FiFolder} color="brandGreen.400" fontSize="20px" flexShrink={0} mr={{ base: 3, md: 4 }} aria-hidden />
          {(status === 'success' && data) || activeProject ? (
            <>
              <Box minW={0}>
                {/* item 553 — the project NAME is the primary label (Dashboard breadcrumb removed) */}
                <Breadcrumb separator={<Box as={FiChevronRight} color={subColor} fontSize="12px" />} fontSize="sm">
                  <BreadcrumbItem isCurrentPage={!docLabel}>
                    <BreadcrumbLink
                      color={docLabel ? crumbColor : nameColor}
                      fontWeight="bold"
                      onClick={() => ctxId && history.push(`/project/${ctxId}`)}
                    >
                      {name}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  {docLabel && (
                    <BreadcrumbItem isCurrentPage>
                      <Text color={nameColor} fontWeight="bold">{docLabel}</Text>
                    </BreadcrumbItem>
                  )}
                </Breadcrumb>
                {data?.projectId && (
                  <Text fontSize="xs" color={subColor} noOfLines={1}>
                    {data.projectId}
                    {data.customer?.name ? ` · ${data.customer.name}` : ''}
                  </Text>
                )}
              </Box>
              {/* item 691/703 (Chad) — quick project money as rubber STAMPS on the LEFT (after the name):
                  amber "PENDING $X" (not yet billed) + green "INVOICED $Y" (finalized). */}
              {/* item 1089 (Chad's phone pass — "opportunity, to bill, invoiced and RNWY is
                  one of our largest challenges"): the chip group takes its OWN full-width
                  line on phones and wraps chip-by-chip (Flex gap wraps reliably; Stack v1's
                  margin spacing fought flexWrap — that's why INVOICED ran off-screen). */}
              <Flex
                ml={{ base: 0, lg: 8 }}
                mt={{ base: 1, lg: 0 }}
                align="center"
                wrap="wrap"
                minW={0}
                flexBasis={{ base: '100%', lg: 'auto' }}
                sx={{ gap: '6px' }}
              >
                {/* item 875 (Chad: "a lot of styles happening") — ONE pill family for the whole
                    header: the rotated rubber stamps are retired; money + RNWY + status all
                    read as the same quiet, color-coded tags. */}
                {pendingTotal > 0 && headerPill('orange', `OPPORTUNITY ${money(pendingTotal)}`)}
                {toBill > 0 && headerPill('red', `TO BILL ${money(toBill)}`)}
                {finalizedInvoiced > 0 && headerPill('green', `INVOICED ${money(finalizedInvoiced)}`)}
                {/* item 1432 — SignWell-sent contracts still waiting on a signature */}
                {awaitingSignature > 0 && (
                  <Tooltip label={`${awaitingSignature} contract${awaitingSignature === 1 ? '' : 's'} sent for signature at SignWell and not yet signed — see CONTRACTS below.`}>
                    <Box>{headerPill('orange', `${awaitingSignature} AWAITING SIGNATURE`)}</Box>
                  </Tooltip>
                )}
                {/* item 1170/1292 — GP = invoiced − commissions − hardware distro cost */}
                {isAdminEyes && finalizedInvoiced > 0 && (
                  <Tooltip label={`Gross profit = invoiced ${money(gpInvoiced)} − commissions ${money(projectCommissions)} − hardware cost ${money(hardwareCost)} (QB-backfill imports excluded — no cost data; commissions: all reasons, reversed rows excluded; hardware at distro cost)`}>
                    <Box>{headerPill(grossProfit >= 0 ? 'teal' : 'red', `GP ${money(grossProfit)}`)}</Box>
                  </Tooltip>
                )}
                {/* item 861/875 — live RNWY capacity: the RNWY LOGO (masked, health-tinted)
                    + "N of M venues available"; hover = everything the status API gives us. */}
                {rnwyRow && (
                  <Tooltip label={rnwyTipLabel}>
                    <Tag
                      size="sm"
                      // item 876 (Chad: "not two greens next to each other") — the RNWY chip is a
                      // SYSTEM metric, not money: it wears the RNWY brand PURPLE as its identity,
                      // and health (green/amber/red) lives in the small dot inside it. Green stays
                      // the health signal without a second green field beside INVOICED.
                      colorScheme={rnwyRow.ok ? 'purple' : 'gray'}
                      variant="subtle"
                      borderRadius="xl"
                      px={2}
                      py={1}
                      flexShrink={0}
                      fontSize="0.68rem"
                      fontWeight="bold"
                      letterSpacing="0.04em"
                      whiteSpace="nowrap"
                      cursor={isAdminEyes ? 'pointer' : 'default'}
                      onClick={isAdminEyes ? () => setRnwyLinkOpen(true) : undefined}
                      boxShadow={aurora && rnwyRow.ok ? '0 0 12px rgba(99,50,251,0.40)' : 'none'}
                    >
                      {/* the wordmark as a CSS mask so it tints to the health color (currentColor) */}
                      <Box
                        w="34px"
                        h="11px"
                        mr={1.5}
                        flexShrink={0}
                        aria-label="RNWY"
                        sx={{
                          backgroundColor: 'currentColor',
                          WebkitMaskImage: `url(${rnwyWordmark})`,
                          WebkitMaskRepeat: 'no-repeat',
                          WebkitMaskSize: 'contain',
                          WebkitMaskPosition: 'center',
                          maskImage: `url(${rnwyWordmark})`,
                          maskRepeat: 'no-repeat',
                          maskSize: 'contain',
                          maskPosition: 'center',
                        }}
                      />
                      <TagLabel>
                        {rnwyRow.ok
                          ? `${rnwyRow.venueCapacity} remaining of ${rnwyRow.venueTotalCapacity} venues${rnwyRow.systems.length > 1 ? ` · ${rnwyRow.systems.length} systems` : ''}`
                          : 'no signal'}
                      </TagLabel>
                      {/* item 876 — the health traffic light (green = room, amber ≤5, red ≤2) */}
                      {rnwyRow.ok && (
                        <Box
                          w="7px"
                          h="7px"
                          ml={1.5}
                          borderRadius="full"
                          flexShrink={0}
                          bg={`${rnwyScheme}.400`}
                          boxShadow={aurora ? `0 0 8px ${rnwyGlow}` : 'none'}
                        />
                      )}
                    </Tag>
                  </Tooltip>
                )}
              </Flex>
              {/* item 861 — admin: link an unlinked project to its deployed RNWY system */}
              {isAdminEyes && onProjectLanding && !rnwySystemId && fleet?.enabled !== false && (
                <Button
                  size="xs"
                  variant="ghost"
                  leftIcon={<FiRadio />}
                  ml="auto"
                  flexShrink={0}
                  opacity={0.75}
                  onClick={() => setRnwyLinkOpen(true)}
                >
                  LINK RNWY
                </Button>
              )}
              {/* item 703 — Report + status pushed to the far right (stamps now sit on the left) */}
              <Button
                size="xs"
                variant="outline"
                leftIcon={<FiFileText />}
                ml={isAdminEyes && onProjectLanding && !rnwySystemId && fleet?.enabled !== false ? 2 : 'auto'}
                flexShrink={0}
                onClick={() => {
                  openPdf(`project/${ctxId}/report/pdf`).catch(() => toast({
                    status: 'error', title: 'Could not open the report', duration: 6000, isClosable: true,
                  }));
                }}
              >
                Report
              </Button>
              {data?.status === ProjectStatus.Closed && (
                <Button
                  size="xs"
                  colorScheme="brandGreen"
                  leftIcon={<FiRotateCcw />}
                  ml={2}
                  flexShrink={0}
                  onClick={() => {
                    setProjectReopened(ctxId).catch(() => toast({
                      status: 'error', title: 'Could not reopen the project', duration: 6000, isClosable: true,
                    }));
                  }}
                >
                  Reopen
                </Button>
              )}
              {st && (
                <Tag
                  size="sm"
                  colorScheme={st.scheme}
                  variant="subtle"
                  borderRadius="xl"
                  px={2}
                  py={1}
                  ml={2}
                  flexShrink={0}
                  fontSize="0.68rem"
                  fontWeight="bold"
                  letterSpacing="0.04em"
                  textTransform="uppercase"
                  boxShadow={aurora ? `0 0 12px ${st.glow}` : 'none'}
                  border={aurora ? undefined : '1px solid'}
                  borderColor={aurora ? undefined : `${st.scheme}.300`}
                >
                  <TagLabel>{st.label}</TagLabel>
                </Tag>
              )}
            </>
          ) : (
            <Skeleton height="16px" width="220px" borderRadius="md" />
          )}
        </Flex>
        {/* item 602 — the section quick-jump pills (Customer/Proposals/Hand-offs/… "focus on
            navigation" row) were removed as clunky (Chad); the project bar itself — name,
            breadcrumb, status, Report — stays. */}
      </Flex>
      {/* item 861 — admin link/relink/unlink modal */}
      {ctxId && (
        <RnwyLinkModal
          isOpen={rnwyLinkOpen}
          onClose={() => setRnwyLinkOpen(false)}
          projectId={ctxId}
          projectName={name}
          currentSystemId={rnwySystemId}
        />
      )}
    </Box>
  );
};

export default ProjectContextBar;
